import React from 'react';
import InovxLoader from '@shared/global/InovxLoader';
import {
    PlatformSessionNoAccessError,
    PlatformSessionRegistrationError,
    PlatformSessionRegistrationProfile,
    registerExternalPlatformSession,
} from './platformSession';
import '@styles/login.less';

interface SelfRegistrationPageProps {
    registration: PlatformSessionRegistrationProfile;
    onRegistered: () => void;
}

function getRegistrationErrorMessage(error: PlatformSessionRegistrationError) {
    switch (error.response?.Reason?.toLowerCase()) {
        case 'passworddoesnotmatch':
            return InovxLoader.t('common:auth.passwordsDoNotMatch');
        case 'passwordrequired':
            return InovxLoader.t('common:auth.newPasswordRequired');
        case 'noaccess':
            return InovxLoader.t('common:auth.selfRegistrationNotAllowed');
        case 'invalidregistration':
            return InovxLoader.t('common:auth.invalidRegistration');
        case 'passwordruleviolation':
            return InovxLoader.t('common:auth.passwordRuleViolation');
        default:
            return error.response?.Message || InovxLoader.t('common:auth.registrationFailed');
    }
}

const SelfRegistrationPage: React.FC<SelfRegistrationPageProps> = ({ registration, onRegistered }) => {
    const [email, setEmail] = React.useState(registration.Email || '');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [firstName, setFirstName] = React.useState(registration.FirstName || '');
    const [lastName, setLastName] = React.useState(registration.LastName || '');
    const [errorMessage, setErrorMessage] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        setEmail(registration.Email || '');
        setFirstName(registration.FirstName || '');
        setLastName(registration.LastName || '');
        setNewPassword('');
        setConfirmPassword('');
        setErrorMessage('');
    }, [registration]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage('');

        if (registration.EnablePasswordEntry && newPassword !== confirmPassword) {
            setErrorMessage(InovxLoader.t('common:auth.passwordsDoNotMatch'));
            return;
        }

        setSubmitting(true);
        try {
            await registerExternalPlatformSession({
                email,
                firstName,
                lastName,
                newPassword,
                confirmPassword,
            });
            onRegistered();
        } catch (error) {
            if (error instanceof PlatformSessionRegistrationError) {
                setErrorMessage(getRegistrationErrorMessage(error));
            } else if (error instanceof PlatformSessionNoAccessError) {
                setErrorMessage(InovxLoader.t('common:auth.selfRegistrationNotAllowed'));
            } else if (error instanceof Error) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(InovxLoader.t('common:auth.registrationFailed'));
            }
        } finally {
            setSubmitting(false);
            setNewPassword('');
            setConfirmPassword('');
        }
    };

    return (
        <div className="login-page-container">
            <div className="login-inner">
                <div className="login-panel panel registration-panel">
                    <div className="login-header registration-header">
                        <div className="welcome-text">{InovxLoader.t('common:auth.welcomeTo')}</div>
                        <div className="vlogo registration-logo">
                            <img alt="V-Suite" src="/images/visionaize-login-logo.png" />
                        </div>
                        <div className="signin-text registration-title">{InovxLoader.t('common:auth.userRegistration')}</div>
                        <div className="registration-for">
                            {InovxLoader.t('common:auth.registrationFor')}
                            <div className="registration-username">{registration.Username}</div>
                        </div>
                    </div>
                    <form className="form-horizontal registration-form" role="form" onSubmit={handleSubmit}>
                        {errorMessage && (
                            <div className="text-danger registration-error" role="alert">
                                {errorMessage}
                            </div>
                        )}

                        <div className="input-group has-info">
                            <div className="input-group-addon"><span className="glyphicon glyphicon-envelope" title={InovxLoader.t('common:auth.email')}></span></div>
                            <input
                                className="form-control"
                                type="email"
                                placeholder={InovxLoader.t('common:auth.email')}
                                title={InovxLoader.t('common:auth.email')}
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                            />
                        </div>

                        {registration.EnablePasswordEntry && (
                            <>
                                <div className="input-group has-info">
                                    <div className="input-group-addon"><span className="glyphicon glyphicon-lock" title={InovxLoader.t('common:auth.newPassword')}></span></div>
                                    <input
                                        className="form-control"
                                        type="password"
                                        placeholder={InovxLoader.t('common:auth.newPassword')}
                                        title={InovxLoader.t('common:auth.newPassword')}
                                        required
                                        autoComplete="new-password"
                                        value={newPassword}
                                        onChange={(event) => setNewPassword(event.target.value)}
                                    />
                                </div>

                                <div className="input-group has-info">
                                    <div className="input-group-addon"><span className="glyphicon glyphicon-lock" title={InovxLoader.t('common:auth.confirmNewPassword')}></span></div>
                                    <input
                                        className="form-control"
                                        type="password"
                                        placeholder={InovxLoader.t('common:auth.confirmNewPassword')}
                                        title={InovxLoader.t('common:auth.confirmNewPassword')}
                                        required
                                        autoComplete="new-password"
                                        value={confirmPassword}
                                        onChange={(event) => setConfirmPassword(event.target.value)}
                                    />
                                </div>
                            </>
                        )}

                        <div className="input-group has-info">
                            <div className="input-group-addon"><span className="glyphicon glyphicon-user" title={InovxLoader.t('common:auth.firstName')}></span></div>
                            <input
                                className="form-control"
                                type="text"
                                placeholder={InovxLoader.t('common:auth.firstName')}
                                title={InovxLoader.t('common:auth.firstName')}
                                autoComplete="given-name"
                                value={firstName}
                                onChange={(event) => setFirstName(event.target.value)}
                            />
                        </div>

                        <div className="input-group has-info">
                            <div className="input-group-addon"><span className="glyphicon glyphicon-user" title={InovxLoader.t('common:auth.lastName')}></span></div>
                            <input
                                className="form-control"
                                type="text"
                                placeholder={InovxLoader.t('common:auth.lastName')}
                                title={InovxLoader.t('common:auth.lastName')}
                                autoComplete="family-name"
                                value={lastName}
                                onChange={(event) => setLastName(event.target.value)}
                            />
                        </div>

                        <button id="registerButton" type="submit" className="btn btn-info btn-width-full register-button" disabled={submitting}>
                            {submitting ? InovxLoader.t('common:auth.registering') : InovxLoader.t('common:auth.registerUser')}
                        </button>
                    </form>
                </div>
            </div>
            <p className="inovx-copyright">{InovxLoader.t('common:about.copyright')}</p>
        </div>
    );
};

export default SelfRegistrationPage;
